import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

import CheckoutCart from './CheckoutCart';
import CheckoutCustomerDetails from './CheckoutCustomerDetails';
import CheckoutPayment from './CheckoutPayment';
import CheckoutSuccess from './CheckoutSuccess';
import { initializePaddle } from '@paddle/paddle-js';
import config from '../config';
import './PricingPage.css';
import logo from '../spherene-logo-white.svg';

import { paddleService } from '../services/paddleService';
import { catalogueService } from '../services/catalogueService';
import { resolveSelection, clampQuantity, productTypeFor, findByPriceId } from '../lib/catalogue';

/**
 * The checkout flow: Cart (edition → plugin → term → users) → Customer details → Payment → Success.
 *
 * The catalogue comes from the middleware's public price list (see lib/catalogue.js); Paddle's
 * PricePreview localizes it in the chosen currency; the checkout is Paddle's overlay with
 * `{ priceId, quantity }`. Deep links: `?sku=COR-12M-RHN-CLD&users=3`, `?edition=COR&platform=TOP&term=1M`,
 * and the older `?priceId=…` (resolved while that price is active). `?mode=payment&customerId=…&priceId=…`
 * is the HubSpot payment-link mode that jumps straight to payment for a known Paddle customer.
 */
const PricingPage = () => {
  const [catalogue, setCatalogue] = useState(null);   // normalised products with list amounts
  const [products, setProducts] = useState([]);       // the same, localized in the current currency
  const [selectedSku, setSelectedSku] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1: Cart, 2: Details, 3: Payment, 4: Success
  const [customerData, setCustomerData] = useState(null);
  const [paddle, setPaddle] = useState();
  const paddleRef = React.useRef(); // Use ref to access paddle instance in callbacks
  const [checkoutData, setCheckoutData] = useState(null);
  const [error, setError] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isPaymentLink, setIsPaymentLink] = useState(false);
  const [externalCustomerId, setExternalCustomerId] = useState(null);
  const [externalAddressId, setExternalAddressId] = useState(null);

  // Currency State
  const [currency, setCurrency] = useState('EUR');
  const [isPriceLoading, setIsPriceLoading] = useState(true);
  const [isPaddleInitializing, setIsPaddleInitializing] = useState(true);

  useEffect(() => {
    // Check for API Key (Token) first.
    let paddleAuthOptions;

    // Support Secret Keys (pdl_) OR Client-side Tokens (test_, live_)
    if (config.paddleApiKey && (
      config.paddleApiKey.startsWith('pdl_') ||
      config.paddleApiKey.startsWith('test_') ||
      config.paddleApiKey.startsWith('live_')
    )) {
      paddleAuthOptions = { token: config.paddleApiKey };
    } else if (config.paddleSellerId) {
      // Fallback to Seller ID
      const parsedSellerId = Number(config.paddleSellerId);
      paddleAuthOptions = Number.isFinite(parsedSellerId) && parsedSellerId > 0
        ? { seller: parsedSellerId }
        : { token: config.paddleSellerId };
    } else {
      setError('Paddle configuration missing (API Key or Seller ID).');
      setIsPaddleInitializing(false); // Fail gracefully
      return;
    }

    initializePaddle({
      environment: config.paddleEnv,
      eventCallback: (data) => {
        if (data.name === 'checkout.completed') {
          setCheckoutData(data.data);
          setCurrentStep(4);
          // Close the overlay using the ref
          paddleRef.current?.Checkout.close();
          setIsPaymentLoading(false);
        } else if (data.name === 'checkout.closed') {
          setIsPaymentLoading(false);
        }
      },
      ...paddleAuthOptions,
    })
      .then((paddleInstance) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
          paddleRef.current = paddleInstance;
        }
      })
      .catch((err) => {
        setError('Failed to initialize Paddle.');
        console.error(err);
      })
      .finally(() => {
        setIsPaddleInitializing(false);
      });
  }, []);

  // The catalogue is loaded once; the URL decides what is selected first.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const list = await catalogueService.fetchCatalogue();
        if (cancelled) return;
        setCatalogue(list);

        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');
        const customerId = params.get('customerId');
        const addressId = params.get('addressId');

        if (mode === 'payment' && customerId) {
          // Payment link for an existing Paddle customer: the price id must still be live.
          const linked = findByPriceId(list, params.get('priceId'));
          if (!linked) {
            setError('This payment link points to an offer that is no longer available. Please ask us for a new link.');
            return;
          }
          setIsPaymentLink(true);
          setExternalCustomerId(customerId);
          if (addressId) {
            setExternalAddressId(addressId);
          }
          setSelectedSku(linked.sku);
          setQuantity(clampQuantity(linked, params.get('users') || params.get('quantity') || 1));
          setCurrentStep(3); // Jump directly to payment
          return;
        }

        const { product, quantity: initialQuantity, notes: resolutionNotes } = resolveSelection(list, params);
        if (!product) {
          setError(list.length === 0
            ? 'No offers are available at the moment. Please try again later or contact sales@spherene.io.'
            : 'Product not found.');
          return;
        }
        setSelectedSku(product.sku);
        setQuantity(initialQuantity);
        setNotes(resolutionNotes);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load the catalogue. Please try again later.');
          console.error(err);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Localized unit prices whenever Paddle, the currency or the catalogue changes.
  useEffect(() => {
    if (!catalogue) return undefined;
    let cancelled = false;
    setIsPriceLoading(true);
    paddleService.localizePrices(paddle, catalogue, currency).then((localized) => {
      if (cancelled) return;
      setProducts(localized);
      setIsPriceLoading(false);
    });
    return () => { cancelled = true; };
  }, [catalogue, paddle, currency]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.sku === selectedSku) || null,
    [products, selectedSku],
  );

  const syncUrl = useCallback((sku, users) => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('sku', sku);
    newUrl.searchParams.delete('priceId');
    if (users > 1) {
      newUrl.searchParams.set('users', String(users));
    } else {
      newUrl.searchParams.delete('users');
    }
    window.history.replaceState({}, '', newUrl);
  }, []);

  const handleSelect = (product) => {
    if (!product) return;
    const nextQuantity = clampQuantity(product, quantity);
    setSelectedSku(product.sku);
    setQuantity(nextQuantity);
    setNotes([]);
    syncUrl(product.sku, nextQuantity);
  };

  const handleQuantityChange = (value) => {
    if (!selectedProduct) return;
    const nextQuantity = clampQuantity(selectedProduct, value);
    setQuantity(nextQuantity);
    syncUrl(selectedProduct.sku, nextQuantity);
  };

  const handleCurrencyChange = (e) => {
    setCurrency(e.target.value);
  };

  const handleCustomerDetailsSubmit = async (data) => {
    setCustomerData(data);
    try {
      const payload = {
        ...data,
        productType: productTypeFor(selectedProduct),
      };

      const response = await axios.post(`${config.hubspotApiUrl}/save-customer-stage`, payload);
      if (response.data && response.data.hubspotId) {
        setCustomerData((prev) => ({ ...prev, hubspotContactId: response.data.hubspotId }));
      }
    } catch (err) {
      console.error('Failed to save customer stage', err);
    }
    setCurrentStep(3);
  };

  const handlePaymentSubmit = () => {
    setIsPaymentLoading(true);

    const checkoutSettings = {
      items: [
        {
          priceId: selectedProduct.priceId,
          // "N users" is quantity N of the one price: one licence with N × seats.
          quantity,
        },
      ],
      settings: {
        allowDiscountRemoval: true,
        showAddDiscounts: true,
        successUrl: null, // Disable redirect to handle inline
      },
    };

    if (isPaymentLink && externalCustomerId) {
      // Use existing customer ID
      checkoutSettings.customer = {
        id: externalCustomerId,
      };
      if (externalAddressId) {
        checkoutSettings.customer.address = { id: externalAddressId };
      }
    } else {
      const customer = {
        email: customerData.email,
        address: {
          firstLine: customerData.firstLine,
          city: customerData.city,
          region: customerData.region,
          countryCode: customerData.countryCode,
          postalCode: customerData.zip,
        },
      };

      if (customerData.company) {
        customer.business = {
          name: customerData.company,
          ...(customerData.vatNumber ? { taxIdentifier: customerData.vatNumber } : {}),
        };
      }

      checkoutSettings.customer = customer;
      checkoutSettings.customData = {
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        hubspotContactId: customerData.hubspotContactId,
      };
    }

    paddle?.Checkout.open(checkoutSettings);

    // We rely on 'checkout.closed' / 'checkout.completed' to reset; but if Paddle
    // isn't initialized nothing will open, so reset immediately.
    if (!paddle) {
      setIsPaymentLoading(false);
    }
  };

  if (error) {
    return <div className="pricing-page"><div className="error-message">{error}</div></div>;
  }

  if (!selectedProduct) {
    return <div className="pricing-page">Loading...</div>;
  }

  if (currentStep === 4) {
    return (
      <div className="pricing-page container">
        <CheckoutSuccess
          checkoutData={checkoutData}
          customerData={customerData}
          product={selectedProduct}
          quantity={quantity}
        />
      </div>
    );
  }

  return (
    <>
      <header className="checkout-header">
        <img src={logo} alt="Spherene" />
      </header>
      <div className="pricing-page container">

        {/* Step 1: Cart */}
        {!isPaymentLink && (
          currentStep === 1 ? (
            <CheckoutCart
              products={products}
              product={selectedProduct}
              quantity={quantity}
              notes={notes}
              onSelect={handleSelect}
              onQuantityChange={handleQuantityChange}
              onEdit={() => setCurrentStep(1)}
              currency={currency}
              onCurrencyChange={handleCurrencyChange}
              loading={isPriceLoading || isPaddleInitializing}
            />
          ) : (
            <div className="checkout-step completed" onClick={() => setCurrentStep(1)}>
              <div className="step-header">
                <div className="step-number-container"><div className="step-number">✓</div></div>
                <h3>Cart</h3>
                <button className="edit-btn">Edit</button>
              </div>
            </div>
          )
        )}

        {/* Step 2: Customer Details */}
        {!isPaymentLink && (
          currentStep === 1 ? (
            <div className="checkout-step disabled">
              <div className="step-header">
                <div className="step-number-container"><div className="step-number">2</div></div>
                <h3>Customer details</h3>
              </div>
            </div>
          ) : (
            currentStep === 2 ? (
              <CheckoutCustomerDetails
                product={selectedProduct}
                onContinue={handleCustomerDetailsSubmit}
                initialData={customerData}
              />
            ) : (
              <div className="checkout-step completed" onClick={() => setCurrentStep(2)}>
                <div className="step-header">
                  <div className="step-number-container"><div className="step-number">✓</div></div>
                  <h3>Customer details</h3>
                  <button className="edit-btn">Edit</button>
                </div>
              </div>
            )
          )
        )}

        {/* Step 3: Payment */}
        {currentStep < 3 && (
          <div className="checkout-step disabled">
            <div className="step-header">
              <div className="step-number-container"><div className="step-number">3</div></div>
              <h3>Payment</h3>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <CheckoutPayment
            product={selectedProduct}
            quantity={quantity}
            customerData={customerData}
            onSubmit={handlePaymentSubmit}
            loading={isPaymentLoading}
          />
        )}

        {/* Step 1 Continue Button (Only visible in Step 1) */}
        {currentStep === 1 && (
          <div style={{ textAlign: 'right' }}>
            <button className="continue-btn" onClick={() => setCurrentStep(2)} style={{ width: 'auto' }} disabled={isPriceLoading}>
              Continue
            </button>
          </div>
        )}

      </div>
    </>
  );
};

export default PricingPage;
